import { useCallback, useState } from "react";

// Chosen drawing speed (seconds to draw ONE stroke) persists across pictures,
// so a speed picked on one drawing carries over to the next one the child opens.
// Key is versioned because the meaning changed from per-loop to per-stroke.
const SPEED_KEY = "learn-draw:speed-v2";
// Default to the slowest speed (the slider's far-left 🐢) so the strokes draw
// nice and slow for little ones until a grown-up speeds them up. Matches the
// slider's slow end (SLOW = 9 in Controls).
const DEFAULT_DURATION = 9; // seconds per stroke — strokes draw one at a time

// "Still picture" mode: instead of the strokes drawing themselves, each step is
// shown as its finished still image. Like the speed, it's a grown-up preference
// that persists across drawings and sessions until switched back to Animation.
const STILL_KEY = "learn-draw:still";

function loadDuration(): number {
  const n = Number(localStorage.getItem(SPEED_KEY));
  // Keep within the slider's range (1s fast … 9s slow); fall back to default.
  return Number.isFinite(n) && n >= 1 && n <= 9 ? n : DEFAULT_DURATION;
}

function loadStill(): boolean {
  try {
    return localStorage.getItem(STILL_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Owns the tutorial playback state: which step we're on and how fast the
 * strokes draw. Speed is expressed as the loop duration in seconds.
 */
export function useDrawingPlayer(stepCount: number) {
  const [stepIndex, setStepIndex] = useState(0);
  // Seconds for one draw loop. Lower = faster. Remembered from last time.
  const [duration, setDurationState] = useState<number>(loadDuration);
  // When paused, the current step's strokes freeze mid-draw instead of looping.
  const [paused, setPaused] = useState(false);
  // "Still picture" vs "Animation" mode (persisted). When on, steps are shown
  // as finished still images instead of drawing themselves.
  const [still, setStillState] = useState<boolean>(loadStill);

  const setDuration = useCallback((d: number) => {
    setDurationState(d);
    try {
      localStorage.setItem(SPEED_KEY, String(d));
    } catch {
      /* ignore storage errors (e.g. private mode) */
    }
  }, []);

  const toggleStill = useCallback(() => {
    setStillState((s) => {
      const next = !s;
      try {
        localStorage.setItem(STILL_KEY, next ? "1" : "0");
      } catch {
        /* ignore storage errors (e.g. private mode) */
      }
      return next;
    });
  }, []);

  const next = useCallback(() => {
    setStepIndex((i) => Math.min(i + 1, stepCount - 1));
  }, [stepCount]);

  const prev = useCallback(() => {
    setStepIndex((i) => Math.max(i - 1, 0));
  }, []);

  const reset = useCallback(() => setStepIndex(0), []);

  const togglePause = useCallback(() => setPaused((p) => !p), []);

  return {
    stepIndex,
    setStepIndex,
    duration,
    setDuration,
    paused,
    setPaused,
    togglePause,
    still,
    toggleStill,
    next,
    prev,
    reset,
    isFirst: stepIndex === 0,
    isLast: stepIndex === stepCount - 1,
  };
}
